import React, { useState, useEffect } from 'react';
import {
  Container,
  Col,
  Row,
  InputGroup,
  Input,
  CustomInput,
  Form,
  FormGroup,
  Label,
  FormFeedback,
  Alert,
  FormText
} from 'reactstrap';
import './_editshiptomapping.scss';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Redirect, useLocation, useHistory } from 'react-router-dom';
import ApolloClient from 'apollo-client';
import AWSAppSyncClient from 'aws-appsync';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import BackButton from '../../components/BackButton/BackButton';
import {
  useAppContext,
} from '../../state/GlobalState';
import {
  EditShipToMappingSchema,
  initialValues,
} from './validation';
import withClient from '../../hoc/withClient';
import './_editshiptomapping.scss';
import pageTitleConstant from '../../analytics/constantPageTitle';
import {
  clearFieldsClickHandler,
  editBackButtonClickHandler,
  editShipToMappingClickHandler
} from '../../analytics/events';
import NetworkSpeedChecker from '../../components/NetworkSpeedChecker/NetworkSpeedChecker';
import { updateCarrierMapping } from '../../api/graphQl/ShipToMapping';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { getLoadingPlants } from '../../api/graphQl/LoadingPlant';

const EditShipToMapping = ({
  graphClient,
}) => {
  const location = useLocation();
  const history = useHistory();
  const {
    registrationEnabled,
    carrierId,
    carrierName,
  } = useAppContext();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingPlants, setLoadingPlants] = useState([]);
  const [enableShiptoEmailField, setEnableShiptoEmailField] = useState(false);
  // Get mapping data from location state
  const mappingData = location.state?.mapping || null;



  if (!registrationEnabled || !mappingData) {
    // If there's no registration enabled or no mapping data, redirect to ship-to-mapping page
    return <Redirect to="/ship-to-mapping" />;
  }

  const fetchLoadingPlants = async () => {
    try {
      const response = await getLoadingPlants(
        graphClient,
        carrierId,
      );
      if (response?.data?.getLoadingPlants?.PLANTS) {
        setLoadingPlants(response.data.getLoadingPlants.PLANTS);
      }
    } catch (error) {
      handleGraphApiError(error);
    }
  };

  const onSubmit = async (values, formikBag) => {

    const inputValues = {
      CarrierId: values.CarrierId,
      SAPPlantId: values.SAPPlantId,
      ShipToNumber: values.ShipToNumber,
      ShipTo: [
        {
          ShipToNumber: values.ShipToNumber,
          ShipToName: values.ShipToName,
          ShipToAddressLine1: values.ShipToAddressLine1,
          ShipToAddressLine2: values.ShipToAddressLine2,
          ShipToAddressLine3: values.ShipToAddressLine3,
          ShipToCity: values.ShipToCity,
          ShipToPostCode: values.ShipToPostCode,
          ShipToEmail: values.ShipToEmail,
          CarrierEmail: values.CarrierEmail,
        }
      ]
    }
    const carrierMappingResult = await updateCarrierMapping(graphClient, inputValues);

    if (carrierMappingResult.data && !carrierMappingResult.error) {
      let message = 'Mapping updated successfully.';
      setErrorMessage('');
      setSuccessMessage(message);
      // Navigate back to ship-to-mapping page after successful update
      setTimeout(() => {
        history.push('/ship-to-mapping');
      }, 1500);
    } else if (carrierMappingResult.error) {
      setErrorMessage(carrierMappingResult.error.replace('GraphQL error: ', 'Error: '));
    } else {
      setErrorMessage('Some error occurred during edit. Please try again later or contact support.');
    }
  };

  const getInitialValues = () => {
    const overrideValues = {
      CarrierId: carrierId || '',
      SAPPlantId: mappingData?.SAPPlantId || '',
      ShipToNumber: mappingData?.ShipToNumber || '',
      ShipToName: mappingData?.ShipToName || '',
      ShipToAddressLine1: mappingData?.ShipToAddressLine1 || '',
      ShipToAddressLine2: mappingData?.ShipToAddressLine2 || '',
      ShipToAddressLine3: mappingData?.ShipToAddressLine3 || '',
      ShipToCity: mappingData?.ShipToCity || '',
      ShipToPostCode: mappingData?.ShipToPostCode || '',
      ShipToEmail: mappingData?.ShipToEmail || '',
      CarrierEmail: mappingData?.CarrierEmail || '',
    };
    // Pre-select values from mapping data
    return Object.assign({}, initialValues, overrideValues);
  };

  const handleFormReset = (resetForm, values) => {
    resetForm({
      values: {
        ...initialValues,
        CarrierId: values.CarrierId,
        CarrierEmail: values.CarrierEmail,
      },
      errors: {}, // clear validation errors explicitly
      touched: {}, // clear touched fields
    });
    setErrorMessage('');
    setSuccessMessage('');
  };

  useEffect(() => {
    if (carrierId) {
      fetchLoadingPlants();
    }
  }, [carrierId]);

  return (
    <Container className="registration-container pl-0 pr-0" data-testid="registration-container">
      <NetworkSpeedChecker />

      <Link className="link" to="/ship-to-mapping" onClick={() => editBackButtonClickHandler(pageTitleConstant.editShipToMappingBackButton)}>
        <BackButton />
      </Link>

      <Container className="page-heading-container p-0" data-testid="page-heading-container">
        <Row>
          <Col>
            <h4>Edit Ship-to Mapping</h4>
          </Col>
        </Row>
      </Container>
      <Row>
        <Col>
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
          {errorMessage && <Alert className="red-alert">{errorMessage}</Alert>}
        </Col>
      </Row>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={EditShipToMappingSchema}
        validateOnMount
        onSubmit={(values, formikBag) => onSubmit(values, formikBag)}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          isSubmitting,
          isValid,
          values,
          errors,
          touched,
          dirty,
          resetForm,
          setFieldValue,
        }) => (
          <Form noValidate onSubmit={handleSubmit} data-testid="add-ship-to-mapping-form">


            <Row>
              {/* Field: Carrier Id */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10 mb-md-8">
                  <Label for="CarrierId">
                    Carrier Id <span className="text-danger">* </span>
                  </Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="CarrierId"
                      data-testid="CarrierId"
                      value={values.CarrierId}
                      disabled
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Carrier Email */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="CarrierEmail">
                    Carrier Email
                  </Label>
                  <div className="input-colour carrier-email-box p-3">
                    {values.CarrierEmail && values.CarrierEmail.split(';').map((email, index) => {
                      const trimmedEmail = email.trim();
                      return (
                        <CustomInput
                          key={index}
                          type="checkbox"
                          id={`carrier-email-${index}`}
                          label={trimmedEmail}
                          checked={true}
                          disabled
                          className="mb-2"
                        />
                      );
                    })}
                    {!values.CarrierEmail && <span className="text-muted">No emails available</span>}
                  </div>
                  <FormText color="help">For carrier email update, please contact administrator.</FormText>
                </FormGroup>
              </Col>
              {/* Field: SAP Plant Id */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="SAPPlantId">
                    SAP Plant Id <span className="text-danger">* </span>
                  </Label>
                  <InputGroup className="input-colour">
                    <CustomInput
                      type="select"
                      name="SAPPlantId"
                      id="SAPPlantId-select"
                      data-testid="SAPPlantId-select"
                      value={values.SAPPlantId}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      invalid={Boolean(errors.SAPPlantId) && touched.SAPPlantId}
                      bsSize="lg"
                      disabled={loadingPlants.length <= 1}
                    >
                      <option key="" value="" hidden>Select an option...</option>
                      {
                        loadingPlants.map(
                          (plant) => (
                            <option
                              key={plant.PlantID}
                              value={plant.PlantID}
                              data-testid={`SAPPlantId-option-${plant.PlantID}`}
                            >
                              {plant.PlantName}
                            </option>
                          ),
                        )
                      }
                    </CustomInput>
                  </InputGroup>
                  {
                    Boolean(errors.SAPPlantId) && touched.SAPPlantId
                    && <FormText color="error">{errors.SAPPlantId}</FormText>
                  }
                </FormGroup>
              </Col>
              {/* Field: Ship To Number */}
              <Col xs="12" md="6">
                <FormGroup>
                  <Label for="ShipToNumber">Ship To Number <span className="text-danger">* </span></Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="ShipToNumber"
                      data-testid="ShipToNumber"
                      value={values.ShipToNumber}
                      invalid={!!errors.ShipToNumber && touched.ShipToNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      maxLength={30}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Ship To Name */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="ShipToName">
                    Ship To Name <span className="text-danger">* </span>
                  </Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="ShipToName"
                      data-testid="ShipToName-field"
                      value={values.ShipToName}
                      invalid={!!errors.ShipToName && touched.ShipToName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      maxLength={50}
                    />
                    <FormFeedback type="invalid" data-testid="ShipToName-feedback">{errors.ShipToName}</FormFeedback>
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Ship To Address Line 1 */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="ShipToAddressLine1">
                    Ship To Address Line 1 <span className="text-danger">* </span>
                  </Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="ShipToAddressLine1"
                      data-testid="ShipToAddressLine1-field"
                      value={values.ShipToAddressLine1}
                      invalid={!!errors.ShipToAddressLine1 && touched.ShipToAddressLine1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      maxLength={70}
                    />
                    <FormFeedback type="invalid" data-testid="ShipToAddressLine1-feedback">{errors.ShipToAddressLine1}</FormFeedback>
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Ship To Address Line 2 */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="ShipToAddressLine2">
                    Ship To Address Line 2
                  </Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="ShipToAddressLine2"
                      data-testid="ShipToAddressLine2-field"
                      value={values.ShipToAddressLine2}
                      invalid={!!errors.ShipToAddressLine2 && touched.ShipToAddressLine2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      maxLength={70}
                    />
                    <FormFeedback type="invalid" data-testid="ShipToAddressLine2-feedback">{errors.ShipToAddressLine2}</FormFeedback>
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Ship To Address Line 3 */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="ShipToAddressLine3">
                    Ship To Address Line 3
                  </Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="ShipToAddressLine3"
                      data-testid="ShipToAddressLine3-field"
                      value={values.ShipToAddressLine3}
                      invalid={!!errors.ShipToAddressLine3 && touched.ShipToAddressLine3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      maxLength={70}
                    />
                    <FormFeedback type="invalid" data-testid="ShipToAddressLine3-feedback">{errors.ShipToAddressLine3}</FormFeedback>
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Ship To City */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="ShipToCity">
                    Ship To City <span className="text-danger">* </span>
                  </Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="ShipToCity"
                      data-testid="ShipToCity-field"
                      value={values.ShipToCity}
                      invalid={!!errors.ShipToCity && touched.ShipToCity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      maxLength={59}
                    />
                    <FormFeedback type="invalid" data-testid="ShipToCity-feedback">{errors.ShipToCity}</FormFeedback>
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Ship To Post Code */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="ShipToPostCode">
                    Ship To Post Code <span className="text-danger">* </span>
                  </Label>
                  <InputGroup className="input-colour">
                    <Input
                      type="text"
                      name="ShipToPostCode"
                      data-testid="ShipToPostCode-field"
                      value={values.ShipToPostCode}
                      invalid={!!errors.ShipToPostCode && touched.ShipToPostCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      maxLength={4}
                    />
                    <FormFeedback type="invalid" data-testid="ShipToPostCode-feedback">{errors.ShipToPostCode}</FormFeedback>
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* Field: Ship To Email */}
              <Col xs="12" md="6">
                <FormGroup className="mb-10">
                  <Label for="ShipToEmail">
                    Ship To Email <span className="text-danger">* </span>
                  </Label>
                  {enableShiptoEmailField ? (
                    <InputGroup className="input-colour">

                      <Input
                        type="email"
                        name="ShipToEmail"
                        data-testid="ShipToEmail-field"
                        value={values.ShipToEmail}
                        invalid={!!errors.ShipToEmail && touched.ShipToEmail}
                        onChange={handleChange}
                        onBlur={() => {
                          handleBlur;
                          if (!errors.ShipToEmail) {
                            setEnableShiptoEmailField(false);
                          }
                        }}
                        autoComplete="off"
                        maxLength={254}
                      />
                    </InputGroup>
                    ) : (
                      <div className="input-colour carrier-email-box p-3" onClick={() => setEnableShiptoEmailField(true)}>
                        {values.ShipToEmail && values.ShipToEmail.split(';').map((email, index) => {
                          const trimmedEmail = email.trim();
                          return (
                            <CustomInput
                              key={index}
                              type="checkbox"
                              id={`carrier-email-${index}`}
                              label={trimmedEmail}
                              checked={true}
                              disabled
                              className="mb-2"
                            />
                          );
                        })}
                        {!values.ShipToEmail && <span className="text-muted">No emails available</span>}
                      </div>
                    )}
                  {
                    errors.ShipToEmail && touched.ShipToEmail
                      ? <FormText color="error">{errors.ShipToEmail}</FormText>
                      : <FormText color="help">If you need to add multiple ship to emails, separate them with a semicolon(;).</FormText>
                  }
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs="12">
                <PrimaryButton
                  disabled={isSubmitting || !isValid || !dirty}
                  className="submit-button"
                  data-testid="edit-mapping-button"
                  type="Submit"
                  label="Update Mapping"
                  tabIndex={0}
                  onClick={() => editShipToMappingClickHandler(pageTitleConstant.editShipToMappingForm)}
                />
              </Col>
              <Col xs="12" className="mt-6">
                <div
                  className="green-link"
                  data-testid="clear-all-link"
                  onClick={() => {
                    handleFormReset(resetForm, values);
                    clearFieldsClickHandler(pageTitleConstant.editShipToMappingForm);
                  }}
                  role="button"
                >
                  Clear all fields
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

/**
 * We use oneOfType because
 * the application uses AppSyncClient
 * the tests/jest provide ApolloClient as we use the <MockProvider/>
 */
EditShipToMapping.propTypes = {
  graphClient: PropTypes.oneOfType([
    PropTypes.instanceOf(AWSAppSyncClient).isRequired,
    PropTypes.instanceOf(ApolloClient).isRequired,
  ]).isRequired,
};

// withClient will provide the client in props
export default withClient(EditShipToMapping);
